import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { Shield, Trash2, MapPin, Clock, User, AlertTriangle, Calendar, ChevronDown, ChevronUp, Package, Siren, Users, Building2, Ship, X, Eye, Pencil, Check, Scale, Camera, Image as ImageIcon, Save, Trophy, Upload, Sparkles, FileText, Plus } from 'lucide-react';
import { toast } from 'sonner';
import NotificationAdmin from './NotificationAdmin';
import AnnouncementAdmin from './AnnouncementAdmin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Modal per modifica raccolta (Admin)
const CollectionEditModal = ({ collectionId, token, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collection, setCollection] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    const fetchCollection = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/collections/${collectionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCollection(response.data);
        
        // Normalizza le categorie (converti stringhe con virgola in numeri)
        const rawCategories = response.data.categories || {};
        const normalizedCategories = {};
        Object.keys(rawCategories).forEach(key => {
          let val = rawCategories[key];
          if (typeof val === 'string') {
            val = parseFloat(val.replace(',', '.')) || 0;
          }
          normalizedCategories[key] = val || 0;
        });
        
        setForm({
          kg_collected: parseFloat(response.data.kg_collected) || 0,
          place: response.data.place || '',
          volunteer_name: response.data.volunteer_name || '',
          photo_url: response.data.photo_url || '',
          difficulty: response.data.difficulty || '',
          pollution_level: response.data.pollution_level || '',
          animal_sightings: response.data.animal_sightings || '',
          hours_spent: parseFloat(response.data.hours_spent) || 0,
          is_sea_collection: response.data.is_sea_collection || false,
          participants_count: parseInt(response.data.participants_count) || 1,
          notes: response.data.notes || '',
          categories: normalizedCategories
        });
      } catch (error) {
        toast.error('Errore nel caricamento raccolta');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchCollection();
  }, [collectionId, token, onClose]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Calcola totale kg dalle categorie se presenti
      const categoriesTotal = Object.values(form.categories || {}).reduce((sum, val) => {
        const num = parseFloat(val) || 0;
        return sum + num;
      }, 0);
      
      // Usa il totale delle categorie se maggiore di 0, altrimenti usa kg_collected manuale
      const finalKg = categoriesTotal > 0 ? categoriesTotal : form.kg_collected;
      
      const dataToSave = {
        ...form,
        kg_collected: parseFloat(finalKg.toFixed(2))
      };
      
      await axios.put(`${BACKEND_URL}/api/admin/collections/${collectionId}`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Raccolta aggiornata con successo!');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-cyan-500/50 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-cyan-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            MODIFICA RACCOLTA
          </h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info originali */}
        <div className="bg-slate-800/50 rounded-xl p-3 mb-4 text-xs text-slate-400">
          <p>ID: {collection?.id}</p>
          <p>Utente: {collection?.user_id}</p>
          <p>Data: {collection?.timestamp ? new Date(collection.timestamp).toLocaleString('it-IT') : 'N/A'}</p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Nome Volontario */}
          <div>
            <label className="text-xs text-slate-400 uppercase block mb-1">Nome Volontario</label>
            <input
              value={form.volunteer_name}
              onChange={(e) => setForm({...form, volunteer_name: e.target.value})}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
              placeholder="Nome del volontario"
            />
          </div>

          {/* KG Raccolti */}
          <div>
            <label className="text-xs text-slate-400 uppercase block mb-1">KG Raccolti</label>
            <input
              type="number"
              step="0.1"
              value={form.kg_collected}
              onChange={(e) => setForm({...form, kg_collected: parseFloat(e.target.value) || 0})}
              className="w-full px-3 py-2 bg-slate-800 border border-cyan-500/50 rounded-lg text-white text-xl font-bold"
            />
          </div>

          {/* Luogo */}
          <div>
            <label className="text-xs text-slate-400 uppercase block mb-1">Luogo / Spiaggia</label>
            <input
              value={form.place}
              onChange={(e) => setForm({...form, place: e.target.value})}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
              placeholder="Nome della spiaggia o luogo"
            />
          </div>

          {/* URL Foto */}
          <div>
            <label className="text-xs text-slate-400 uppercase block mb-1">URL Foto</label>
            <input
              value={form.photo_url}
              onChange={(e) => setForm({...form, photo_url: e.target.value})}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
              placeholder="https://..."
            />
            {form.photo_url && (
              <img src={form.photo_url} alt="Preview" className="mt-2 w-32 h-32 object-cover rounded-lg border border-slate-600" />
            )}
          </div>

          {/* Difficoltà e Livello Sporcizia */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 uppercase block mb-1">Difficoltà</label>
              <select
                value={form.difficulty}
                onChange={(e) => setForm({...form, difficulty: e.target.value})}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
              >
                <option value="">Non specificata</option>
                <option value="facile">Facile</option>
                <option value="medio">Medio</option>
                <option value="difficile">Difficile</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 uppercase block mb-1">Livello Sporcizia</label>
              <select
                value={form.pollution_level}
                onChange={(e) => setForm({...form, pollution_level: e.target.value})}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
              >
                <option value="">Non specificato</option>
                <option value="poco">Poco sporco</option>
                <option value="medio">Mediamente sporco</option>
                <option value="tanto">Molto sporco</option>
              </select>
            </div>
          </div>

          {/* Partecipanti e Ore */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 uppercase block mb-1">N° Partecipanti</label>
              <input
                type="number"
                min="1"
                value={form.participants_count}
                onChange={(e) => setForm({...form, participants_count: parseInt(e.target.value) || 1})}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 uppercase block mb-1">Ore Dedicate</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={form.hours_spent}
                onChange={(e) => setForm({...form, hours_spent: parseFloat(e.target.value) || 0})}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
              />
            </div>
          </div>

          {/* Raccolta in mare */}
          <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
            <input
              type="checkbox"
              checked={form.is_sea_collection}
              onChange={(e) => setForm({...form, is_sea_collection: e.target.checked})}
              className="w-5 h-5 accent-orange-500"
            />
            <div>
              <span className="text-white">Raccolta in mare (Fishing for Litter)</span>
              <p className="text-xs text-slate-400">Segna se la raccolta è avvenuta in mare con pescatori</p>
            </div>
          </div>

          {/* Avvistamenti */}
          <div>
            <label className="text-xs text-slate-400 uppercase block mb-1">Avvistamenti Animali</label>
            <textarea
              value={form.animal_sightings}
              onChange={(e) => setForm({...form, animal_sightings: e.target.value})}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white resize-none"
              rows={2}
              placeholder="Eventuali avvistamenti di fauna..."
            />
          </div>

          {/* Categorie Materiali */}
          <div className="border-t border-slate-600 pt-4 mt-4">
            <label className="text-sm text-cyan-400 uppercase font-semibold block mb-3">
              Dettaglio Materiali Raccolti (kg)
            </label>
            <div className="space-y-2">
              {[
                { key: 'plastica_monouso', label: 'Plastica Monouso', color: '#3B82F6' },
                { key: 'mozziconi_cotton', label: 'Mozziconi/Cotton Fioc', color: '#F97316' },
                { key: 'attrezzatura_pesca', label: 'Attrezz. Pesca', color: '#EF4444' },
                { key: 'polistirolo', label: 'Polistirolo', color: '#FBBF24' },
                { key: 'microplastiche', label: 'Microplastiche', color: '#8B5CF6' },
                { key: 'vetro', label: 'Vetro', color: '#06B6D4' },
                { key: 'metalli', label: 'Metalli', color: '#6B7280' },
                { key: 'tessuti', label: 'Tessuti', color: '#EC4899' },
                { key: 'legno_trattato', label: 'Legno Trattato', color: '#78350F' },
                { key: 'gomma', label: 'Gomma', color: '#1F2937' },
                { key: 'carta_cartone', label: 'Carta/Cartone', color: '#D97706' },
                { key: 'organico', label: 'Organico', color: '#65A30D' },
                { key: 'rifiuti_pericolosi', label: 'Rifiuti Pericolosi', color: '#DC2626' },
                { key: 'altro', label: 'Altro', color: '#9CA3AF' }
              ].map((cat) => {
                const catValue = form.categories?.[cat.key];
                const displayValue = catValue !== undefined && catValue !== 0 && catValue !== '' ? catValue : '';
                
                return (
                  <div key={cat.key} className="flex items-center justify-between bg-slate-800/30 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: cat.color }}
                      />
                      <span className="text-sm text-slate-200">{cat.label}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={displayValue}
                        onChange={(e) => {
                          const newVal = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                          setForm(prev => ({
                            ...prev,
                            categories: {
                              ...prev.categories,
                              [cat.key]: newVal
                            }
                          }));
                        }}
                        className="w-16 px-2 py-1 bg-slate-700 border border-slate-500 rounded text-white text-sm text-right"
                        placeholder="0"
                      />
                      <span className="text-xs text-slate-400">kg</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Il totale kg verrà calcolato automaticamente dalla somma.
            </p>
          </div>

          {/* Note Admin */}
          <div>
            <label className="text-xs text-slate-400 uppercase block mb-1">Note Admin</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              className="w-full px-3 py-2 bg-slate-800 border border-amber-500/50 rounded-lg text-white resize-none"
              rows={2}
              placeholder="Note interne per l'amministratore..."
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Salva Modifiche
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
          >
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal per dettagli utente
const UserDetailModal = ({ userId, token, onClose, onUpdate, onEditCollection }) => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserData(response.data);
        setEditForm({
          name: response.data.profile?.name || '',
          user_type: response.data.profile?.user_type || 'volontario',
          organization_name: response.data.profile?.organization_name || '',
          organization_logo: response.data.profile?.organization_logo || '',
          photo_url: response.data.profile?.photo_url || '',
          team_size: response.data.profile?.team_size || 1,
          accepts_invites: response.data.profile?.accepts_invites !== false,
          phone: response.data.profile?.phone || '',
          city: response.data.profile?.city || '',
          province: response.data.profile?.province || '',
          bio: response.data.profile?.bio || '',
          website: response.data.profile?.website || '',
          fiscal_code: response.data.profile?.fiscal_code || '',
          vat_number: response.data.profile?.vat_number || '',
          pec_email: response.data.profile?.pec_email || ''
        });
      } catch (error) {
        toast.error('Errore nel caricamento dettagli');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchUserDetails();
  }, [userId, token, onClose]);

  const handleSave = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${userId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Utente aggiornato');
      setEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const { profile, stats, recent_collections } = userData || {};

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-cyan-500/50 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-cyan-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            DETTAGLI UTENTE
          </h2>
          <div className="flex gap-2">
            {!editing && (
              <button onClick={() => setEditing(true)} className="p-2 text-amber-400 hover:bg-amber-500/20 rounded-lg">
                <Pencil className="w-5 h-5" />
              </button>
            )}
            <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-700 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Profilo */}
        <div className="bg-slate-800/50 rounded-xl p-4 mb-4">
          {editing ? (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              {/* Nome */}
              <div>
                <label className="text-xs text-slate-400 uppercase">Nome</label>
                <input
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              
              {/* Tipo Utente */}
              <div>
                <label className="text-xs text-slate-400 uppercase">Tipo Utente</label>
                <select
                  value={editForm.user_type}
                  onChange={(e) => setEditForm({...editForm, user_type: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  <option value="volontario">Volontario</option>
                  <option value="insegnante">Insegnante</option>
                  <option value="peschereccio">Peschereccio</option>
                  <option value="sub">Sub / Subacqueo</option>
                  <option value="azienda">Azienda</option>
                  <option value="associazione">Associazione</option>
                  <option value="fondazione">Fondazione</option>
                  <option value="ente">Ente Pubblico</option>
                  <option value="scuola">Scuola</option>
                </select>
              </div>
              
              {/* Organizzazione */}
              <div>
                <label className="text-xs text-slate-400 uppercase">Nome Organizzazione / Azienda</label>
                <input
                  value={editForm.organization_name}
                  onChange={(e) => setEditForm({...editForm, organization_name: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  placeholder="Nome dell'azienda o organizzazione"
                />
              </div>
              
              {/* Logo Organizzazione */}
              <div>
                <label className="text-xs text-slate-400 uppercase">URL Logo Organizzazione</label>
                <input
                  value={editForm.organization_logo}
                  onChange={(e) => setEditForm({...editForm, organization_logo: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-amber-500/50 rounded-lg text-white text-sm"
                  placeholder="https://... (URL immagine logo)"
                />
                {editForm.organization_logo && (
                  <img src={editForm.organization_logo} alt="Logo preview" className="mt-2 w-20 h-20 object-contain bg-white rounded-lg p-1" />
                )}
              </div>
              
              {/* Foto Profilo */}
              <div>
                <label className="text-xs text-slate-400 uppercase">URL Foto Profilo</label>
                <input
                  value={editForm.photo_url}
                  onChange={(e) => setEditForm({...editForm, photo_url: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  placeholder="https://..."
                />
                {editForm.photo_url && (
                  <img src={editForm.photo_url} alt="Foto preview" className="mt-2 w-16 h-16 object-cover rounded-full" />
                )}
              </div>
              
              {/* Città e Provincia */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 uppercase">Città</label>
                  <input
                    value={editForm.city}
                    onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase">Provincia</label>
                  <input
                    value={editForm.province}
                    onChange={(e) => setEditForm({...editForm, province: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="es. VR, MI, RM..."
                  />
                </div>
              </div>
              
              {/* Telefono */}
              <div>
                <label className="text-xs text-slate-400 uppercase">Telefono</label>
                <input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  placeholder="+39..."
                />
              </div>
              
              {/* Sito Web */}
              <div>
                <label className="text-xs text-slate-400 uppercase">Sito Web</label>
                <input
                  value={editForm.website}
                  onChange={(e) => setEditForm({...editForm, website: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  placeholder="https://www.esempio.it"
                />
              </div>
              
              {/* Dati Fiscali */}
              <div className="border-t border-slate-600 pt-3 mt-3">
                <p className="text-xs text-amber-400 uppercase mb-2">Dati Fiscali (Aziende/Enti)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 uppercase">P.IVA</label>
                    <input
                      value={editForm.vat_number}
                      onChange={(e) => setEditForm({...editForm, vat_number: e.target.value})}
                      className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 uppercase">Codice Fiscale</label>
                    <input
                      value={editForm.fiscal_code}
                      onChange={(e) => setEditForm({...editForm, fiscal_code: e.target.value})}
                      className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <label className="text-xs text-slate-400 uppercase">PEC</label>
                  <input
                    value={editForm.pec_email}
                    onChange={(e) => setEditForm({...editForm, pec_email: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="pec@esempio.it"
                  />
                </div>
              </div>
              
              {/* Bio */}
              <div>
                <label className="text-xs text-slate-400 uppercase">Descrizione / Bio</label>
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none"
                  rows={3}
                  placeholder="Breve descrizione..."
                />
              </div>
              
              {/* Team Size */}
              <div>
                <label className="text-xs text-slate-400 uppercase">Dimensione Team</label>
                <input
                  type="number"
                  min="1"
                  value={editForm.team_size}
                  onChange={(e) => setEditForm({...editForm, team_size: parseInt(e.target.value) || 1})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              
              {/* Accetta Inviti */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={editForm.accepts_invites}
                  onChange={(e) => setEditForm({...editForm, accepts_invites: e.target.checked})}
                  className="w-4 h-4 accent-cyan-500"
                />
                <label className="text-slate-300">Accetta inviti ad attività</label>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={handleSave} className="flex-1 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg flex items-center justify-center gap-2">
                  <Check className="w-4 h-4" /> Salva
                </button>
                <button onClick={() => setEditing(false)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg">
                  Annulla
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center">
                {profile?.photo_url || profile?.organization_logo ? (
                  <img src={profile.photo_url || profile.organization_logo} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-slate-400" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{profile?.name || 'Senza nome'}</h3>
                <p className="text-slate-400 text-sm">{profile?.user_id}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    profile?.user_type === 'peschereccio' ? 'bg-orange-500/20 text-orange-400' :
                    profile?.user_type !== 'volontario' ? 'bg-violet-500/20 text-violet-400' :
                    'bg-cyan-500/20 text-cyan-400'
                  }`}>
                    {profile?.user_type || 'Volontario'}
                  </span>
                  {profile?.organization_name && (
                    <span className="text-slate-300 text-sm">{profile.organization_name}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistiche */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-cyan-400">{stats?.collections_count || 0}</p>
            <p className="text-xs text-slate-400">Raccolte</p>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-emerald-400">{stats?.total_kg || 0}</p>
            <p className="text-xs text-slate-400">Kg Raccolti</p>
          </div>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-yellow-400">{stats?.reports_count || 0}</p>
            <p className="text-xs text-slate-400">Segnalazioni</p>
          </div>
          <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-violet-400">{stats?.reports_taken || 0}</p>
            <p className="text-xs text-slate-400">Prese in carico</p>
          </div>
          <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-pink-400">{stats?.events_participated || 0}</p>
            <p className="text-xs text-slate-400">Eventi</p>
          </div>
          <div className="bg-slate-500/10 border border-slate-500/30 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-slate-400">{profile?.accepts_invites !== false ? 'Sì' : 'No'}</p>
            <p className="text-xs text-slate-400">Accetta Inviti</p>
          </div>
        </div>

        {/* Ultime Raccolte */}
        {recent_collections && recent_collections.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">Ultime Raccolte</h4>
            <div className="space-y-2">
              {recent_collections.map((col, idx) => (
                <div key={idx} className="bg-slate-800/30 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{col.place || 'Luogo non specificato'}</p>
                    <p className="text-slate-500 text-xs">{col.date || col.created_at?.split('T')[0]}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-cyan-400 font-bold">{col.kg_collected} kg</span>
                    {col.id && onEditCollection && (
                      <button
                        onClick={() => {
                          onClose();
                          onEditCollection(col.id);
                        }}
                        className="p-1.5 text-amber-400 hover:bg-amber-500/20 rounded-lg transition-all"
                        title="Modifica raccolta"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info registrazione */}
        <div className="mt-4 pt-4 border-t border-slate-700 text-center">
          <p className="text-slate-500 text-xs">
            Registrato il: {profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString('it-IT') : 'N/A'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Componente Classifica Storica (solo Admin)
const LeaderboardTab = ({ token }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/gamification/leaderboard-alltime`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLeaderboard(response.data.leaderboard || []);
      } catch (error) {
        console.error('Error fetching alltime leaderboard:', error);
        toast.error('Errore caricamento classifica');
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/10 border border-amber-500/50 rounded-xl p-4">
        <h3 className="text-amber-400 font-bold flex items-center gap-2 mb-2">
          <Trophy className="w-5 h-5" />
          Classifica Storica di Sempre
        </h3>
        <p className="text-slate-400 text-sm">
          Totale punti e sfide accumulate da tutti gli utenti dall'inizio. Questa classifica è visibile solo a te.
        </p>
      </div>

      {leaderboard.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Nessun dato nella classifica</p>
        </div>
      ) : (
        <div className="space-y-2">
          {leaderboard.map((entry, idx) => (
            <div 
              key={entry.user_email}
              className={`flex items-center gap-3 p-4 rounded-xl ${
                idx < 3 ? 'bg-gradient-to-r from-amber-500/20 to-transparent border border-amber-500/30' : 'bg-slate-800/50'
              }`}
            >
              <div className={`w-10 h-10 flex items-center justify-center font-bold text-lg ${
                idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-slate-300' : idx === 2 ? 'text-amber-600' : 'text-slate-500'
              }`}>
                #{entry.rank}
              </div>
              
              {entry.photoURL ? (
                <img src={entry.photoURL} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                  {entry.displayName?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white truncate">{entry.displayName}</p>
                <p className="text-xs text-slate-500 truncate">{entry.user_email}</p>
                <p className="text-xs text-slate-400">{entry.months_active || 0} mesi attivi</p>
              </div>
              
              <div className="text-right">
                <p className="font-bold text-xl text-amber-400">{entry.total_points || 0}</p>
                <p className="text-xs text-slate-500">punti totali</p>
                <p className="text-xs text-emerald-400">{entry.total_challenges || 0} sfide</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Componente Gestione Immagini Missioni (solo Admin)
const MissionsTab = ({ token }) => {
  const [missions, setMissions] = useState([]);
  const [customizations, setCustomizations] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [missionsRes, customRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/gamification/missions`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${BACKEND_URL}/api/gamification/mission-customizations`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setMissions(missionsRes.data.missions || []);
        setCustomizations(customRes.data.customizations || {});
      } catch (error) {
        console.error('Error fetching missions:', error);
        toast.error('Errore caricamento missioni');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Upload immagine
  const handleImageUpload = async (missionId, file) => {
    if (!file) return;
    
    setUploading(missionId);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        
        await axios.post(
          `${BACKEND_URL}/api/admin/mission-update`,
          { mission_id: missionId, image_url: base64 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setCustomizations(prev => ({
          ...prev,
          [missionId]: { ...prev[missionId], image_url: base64 }
        }));
        toast.success('Immagine caricata!');
        setUploading(null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Errore upload immagine');
      setUploading(null);
    }
  };

  // Inizia modifica titolo/descrizione
  const startEdit = (mission) => {
    const custom = customizations[mission.id] || {};
    setEditForm({
      name: custom.name || mission.name,
      description: custom.description || mission.description
    });
    setEditing(mission.id);
  };

  // Salva titolo/descrizione
  const saveEdit = async (missionId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/mission-update`,
        { 
          mission_id: missionId, 
          name: editForm.name,
          description: editForm.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCustomizations(prev => ({
        ...prev,
        [missionId]: { 
          ...prev[missionId], 
          name: editForm.name,
          description: editForm.description
        }
      }));
      toast.success('Missione aggiornata!');
      setEditing(null);
    } catch (error) {
      toast.error('Errore salvataggio');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-cyan-500/20 to-emerald-500/10 border border-cyan-500/50 rounded-xl p-4">
        <h3 className="text-cyan-400 font-bold flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5" />
          Gestione Missioni Mensili
        </h3>
        <p className="text-slate-400 text-sm">
          Personalizza titolo, descrizione e immagine di ogni missione. Gli utenti vedranno l'immagine coperta da nero che si svela completando le sfide.
        </p>
      </div>

      <div className="space-y-4">
        {missions.map((mission) => {
          const custom = customizations[mission.id] || {};
          const displayName = custom.name || mission.name;
          const displayDesc = custom.description || mission.description;
          const hasImage = custom.image_url;
          const isUploading = uploading === mission.id;
          const isEditing = editing === mission.id;
          
          return (
            <div 
              key={mission.id}
              className="bg-slate-800/50 border border-slate-700 rounded-xl p-4"
            >
              <div className="flex gap-4">
                {/* Anteprima immagine */}
                <div 
                  className="relative w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden"
                  style={{ backgroundColor: mission.color }}
                >
                  {hasImage ? (
                    <img 
                      src={custom.image_url} 
                      alt={displayName} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl opacity-50">
                      🌊
                    </div>
                  )}
                  
                  {isUploading && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <div className="w-6 h-6 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  
                  {/* Pulsante upload sovrapposto */}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/50 transition-all cursor-pointer group">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(mission.id, e.target.files[0])}
                      disabled={isUploading}
                    />
                    <Upload className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </label>
                </div>
                
                {/* Info e form */}
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Titolo missione"
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500"
                      />
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Descrizione"
                        rows={2}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500 resize-none"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveEdit(mission.id)}
                          className="px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm flex items-center gap-1"
                        >
                          <Check className="w-4 h-4" /> Salva
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="px-3 py-1 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-sm"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-white font-bold">{displayName}</h4>
                          <p className="text-slate-400 text-sm">{displayDesc}</p>
                        </div>
                        <button
                          onClick={() => startEdit(mission)}
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          hasImage ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {hasImage ? '✓ Immagine caricata' : '⚠ Immagine mancante'}
                        </span>
                        <span className="text-xs text-slate-500">ID: {mission.id}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-slate-800/50 border border-slate-600 rounded-xl p-4 mt-6">
        <h4 className="text-white font-medium mb-2">📋 Istruzioni</h4>
        <ul className="text-slate-400 text-sm space-y-1">
          <li>• <strong>Immagine:</strong> Passa il mouse sull'anteprima e clicca per caricare</li>
          <li>• <strong>Titolo/Descrizione:</strong> Clicca l'icona matita per modificare</li>
          <li>• Usa immagini quadrate (1:1) a colori vivaci</li>
          <li>• Formati supportati: JPG, PNG, WebP</li>
        </ul>
      </div>
    </div>
  );
};

const AdminSection = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('users');
  const [collections, setCollections] = useState([]);
  const [sosReports, setSosReports] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [wasteReports, setWasteReports] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [alltimeLeaderboard, setAlltimeLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [orgDocuments, setOrgDocuments] = useState([]);
  const [showDocForm, setShowDocForm] = useState(false);
  const [docForm, setDocForm] = useState({
    title: '',
    description: '',
    file_url: '',
    document_type: 'rivista'
  });
  const [orgForm, setOrgForm] = useState({
    name: '',
    logo_url: '',
    admin_email: '',
    admin_password: '',
    invite_code: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [collectionsRes, sosRes, eventsRes, statsRes, usersRes, wasteRes, orgsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/collections`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/sos`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/waste-reports`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/organizations`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setCollections(collectionsRes.data);
      setSosReports(sosRes.data);
      setEvents(eventsRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setWasteReports(wasteRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const deleteCollection = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa raccolta?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/collections/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Raccolta eliminata');
      setCollections(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const deleteSOS = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo report SOS?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/sos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report SOS eliminato');
      setSosReports(prev => prev.filter(s => s.id !== id));
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo evento?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evento eliminato');
      setEvents(prev => prev.filter(e => e.id !== id));
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente? Le sue raccolte e segnalazioni rimarranno nel sistema.')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Utente eliminato');
      setUsers(prev => prev.filter(u => u.user_id !== userId));
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Errore eliminazione utente');
      }
    }
  };

  // Crea organizzazione
  const createOrganization = async () => {
    if (!orgForm.name || !orgForm.admin_email || !orgForm.admin_password || !orgForm.invite_code) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/organizations`, orgForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Organizzazione "${orgForm.name}" creata!`);
      setShowOrgForm(false);
      setOrgForm({ name: '', logo_url: '', admin_email: '', admin_password: '', invite_code: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione');
    }
  };

  // Elimina organizzazione
  const deleteOrganization = async (id, name) => {
    if (!window.confirm(`Eliminare l'organizzazione "${name}"? I membri perderanno l'associazione.`)) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/organizations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Organizzazione eliminata');
      setOrganizations(prev => prev.filter(o => o.id !== id));
      if (selectedOrg?.id === id) {
        setSelectedOrg(null);
        setOrgMembers([]);
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Carica membri organizzazione
  const loadOrgMembers = async (org) => {
    setSelectedOrg(org);
    setLoadingMembers(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/organizations/${org.id}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgMembers(res.data);
      // Carica anche i documenti
      loadOrgDocuments(org.id);
    } catch (error) {
      toast.error('Errore caricamento membri');
      setOrgMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Carica documenti organizzazione
  const loadOrgDocuments = async (orgId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/org-documents/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgDocuments(res.data);
    } catch (error) {
      console.error('Error loading documents:', error);
      setOrgDocuments([]);
    }
  };

  // Aggiungi documento per organizzazione
  const addOrgDocument = async () => {
    if (!docForm.title || !docForm.file_url) {
      toast.error('Inserisci titolo e URL del documento');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/org-documents`, {
        organization_id: selectedOrg.id,
        ...docForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Documento aggiunto!');
      setDocForm({ title: '', description: '', file_url: '', document_type: 'rivista' });
      setShowDocForm(false);
      loadOrgDocuments(selectedOrg.id);
    } catch (error) {
      toast.error('Errore aggiunta documento');
    }
  };

  // Elimina documento
  const deleteOrgDocument = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/org-documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Documento eliminato');
      setOrgDocuments(prev => prev.filter(d => d.id !== docId));
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
          <Shield className="w-6 h-6 text-amber-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            <TranslatedText text="PANNELLO ADMIN" />
          </h2>
          <p className="text-slate-400 text-sm"><TranslatedText text="Gestisci tutti i contenuti dell'app" /></p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-violet-500/30">
            <p className="text-xs text-slate-400 uppercase"><TranslatedText text="Iscritti" /></p>
            <p className="text-2xl font-bold text-violet-400">{stats.total_volunteers}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-cyan-500/30">
            <p className="text-xs text-slate-400 uppercase"><TranslatedText text="Raccolte" /></p>
            <p className="text-2xl font-bold text-cyan-400">{stats.total_collections}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-yellow-500/30">
            <p className="text-xs text-slate-400 uppercase"><TranslatedText text="Segnalazioni" /></p>
            <p className="text-2xl font-bold text-yellow-400">{stats.total_waste_reports || 0}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-red-500/30">
            <p className="text-xs text-slate-400 uppercase">SOS</p>
            <p className="text-2xl font-bold text-red-400">{stats.total_sos}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-emerald-500/30">
            <p className="text-xs text-slate-400 uppercase">Kg Totali</p>
            <p className="text-2xl font-bold text-emerald-400">{stats.total_kg}</p>
          </div>
        </div>
      )}

      {/* Notifiche Push */}
      <NotificationAdmin />

      {/* Comunicazioni Community */}
      <AnnouncementAdmin />

      {/* Pulsante Carica Siti Naturalistici */}
      <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              🌊 Siti Naturalistici Mediterraneo
            </h3>
            <p className="text-slate-400 text-sm">Carica AMP, Centri Recupero Tartarughe e Oasi Faunistiche sulla mappa</p>
          </div>
          <button
            onClick={async () => {
              try {
                const res = await axios.post(`${BACKEND_URL}/api/natural-sites/seed`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success(res.data.message || 'Siti caricati con successo!');
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Errore nel caricamento');
              }
            }}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-all flex items-center gap-2"
          >
            <MapPin className="w-4 h-4" />
            Carica 57 Siti
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveTab('organizations')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'organizations' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
        >
          <Building2 className="w-4 h-4" />
          Organizzazioni ({organizations.length})
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'users' ? 'bg-violet-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
        >
          <Users className="w-4 h-4" />
          Iscritti ({users.length})
        </button>
        <button
          onClick={() => setActiveTab('collections')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'collections' ? 'bg-cyan-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
        >
          <Package className="w-4 h-4" />
          Raccolte ({collections.length})
        </button>
        <button
          onClick={() => setActiveTab('sos')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'sos' ? 'bg-red-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
        >
          <Siren className="w-4 h-4" />
          SOS ({sosReports.length})
        </button>
        <button
          onClick={() => setActiveTab('events')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'events' ? 'bg-violet-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
        >
          <Calendar className="w-4 h-4" />
          Eventi ({events.length})
        </button>
        
        <button
          onClick={() => setActiveTab('leaderboard')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'leaderboard' ? 'bg-amber-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
        >
          <Trophy className="w-4 h-4" />
          Classifica Storica
        </button>
        
        <button
          onClick={() => setActiveTab('missions')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'missions' ? 'bg-cyan-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Missioni
        </button>
      </div>

      {/* Tab Organizzazioni */}
      {activeTab === 'organizations' && (
        <div className="space-y-4">
          {/* Pulsante nuova org */}
          <button
            onClick={() => setShowOrgForm(!showOrgForm)}
            className="w-full py-3 bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 rounded-xl flex items-center justify-center gap-2 hover:bg-emerald-500/30"
          >
            {showOrgForm ? <X className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
            {showOrgForm ? 'Annulla' : 'Nuova Organizzazione'}
          </button>

          {/* Form nuova org */}
          {showOrgForm && (
            <div className="bg-slate-900/80 border border-emerald-500/30 rounded-xl p-4 space-y-4">
              <h3 className="text-lg font-bold text-emerald-400">Crea Organizzazione</h3>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Nome Organizzazione *</label>
                <input
                  type="text"
                  value={orgForm.name}
                  onChange={(e) => setOrgForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Es: Legambiente Liguria"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Logo URL (opzionale)</label>
                <input
                  type="url"
                  value={orgForm.logo_url}
                  onChange={(e) => setOrgForm(p => ({ ...p, logo_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Email Admin *</label>
                  <input
                    type="email"
                    value={orgForm.admin_email}
                    onChange={(e) => setOrgForm(p => ({ ...p, admin_email: e.target.value }))}
                    placeholder="admin@org.it"
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Password *</label>
                  <input
                    type="text"
                    value={orgForm.admin_password}
                    onChange={(e) => setOrgForm(p => ({ ...p, admin_password: e.target.value }))}
                    placeholder="Password123"
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Codice Invito * (per i volontari)</label>
                <input
                  type="text"
                  value={orgForm.invite_code}
                  onChange={(e) => setOrgForm(p => ({ ...p, invite_code: e.target.value.toUpperCase() }))}
                  placeholder="LEGAMBIENTE2024"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white uppercase"
                />
                <p className="text-xs text-slate-500 mt-1">I volontari useranno questo codice per unirsi</p>
              </div>
              
              <button
                onClick={createOrganization}
                className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600"
              >
                Crea Organizzazione
              </button>
            </div>
          )}

          {/* Lista organizzazioni */}
          {organizations.length === 0 ? (
            <p className="text-center text-slate-400 py-8">Nessuna organizzazione creata</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {organizations.map(org => (
                <div 
                  key={org.id} 
                  className={`bg-slate-900/60 border rounded-xl p-4 cursor-pointer transition-all ${
                    selectedOrg?.id === org.id 
                      ? 'border-emerald-500 ring-2 ring-emerald-500/30' 
                      : 'border-slate-700 hover:border-emerald-500/50'
                  }`}
                  onClick={() => loadOrgMembers(org)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {org.logo_url ? (
                        <img src={org.logo_url} alt="" className="w-12 h-12 rounded-lg object-contain bg-white p-1" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-emerald-400" />
                        </div>
                      )}
                      <div>
                        <h4 className="text-white font-semibold">{org.name}</h4>
                        <p className="text-slate-400 text-sm">{org.members_count} membri</p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteOrganization(org.id, org.name);
                      }}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-slate-500 text-xs">Codice Invito</p>
                      <p className="text-cyan-400 font-mono">{org.invite_code}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-slate-500 text-xs">Admin Email</p>
                      <p className="text-white truncate text-xs">{org.admin_email}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Dettagli organizzazione selezionata */}
          {selectedOrg && (
            <div className="mt-6 bg-slate-800/50 border border-emerald-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {selectedOrg.logo_url ? (
                    <img src={selectedOrg.logo_url} alt="" className="w-16 h-16 rounded-lg object-contain bg-white p-1" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-emerald-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-white">{selectedOrg.name}</h3>
                    <p className="text-emerald-400">Codice: {selectedOrg.invite_code}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedOrg(null);
                    setOrgMembers([]);
                  }}
                  className="p-2 text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                Membri ({orgMembers.length})
              </h4>

              {loadingMembers ? (
                <div className="flex justify-center py-4">
                  <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : orgMembers.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Nessun membro registrato</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {orgMembers.map((member, idx) => (
                    <div key={idx} className="bg-slate-900/60 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-400" />
                        <span className="text-white">{member.name || 'Nome non impostato'}</span>
                      </div>
                      <span className="text-xs text-slate-400 bg-slate-700 px-2 py-1 rounded">
                        {member.user_type || 'volontario'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Sezione Documenti */}
              <div className="mt-6 pt-4 border-t border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Documenti ({orgDocuments.length})
                  </h4>
                  <button
                    onClick={() => setShowDocForm(!showDocForm)}
                    className="px-3 py-1 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Aggiungi
                  </button>
                </div>

                {/* Form aggiunta documento */}
                {showDocForm && (
                  <div className="bg-slate-900/60 rounded-lg p-4 mb-4 space-y-3">
                    <input
                      type="text"
                      placeholder="Titolo documento"
                      value={docForm.title}
                      onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Descrizione (opzionale)"
                      value={docForm.description}
                      onChange={(e) => setDocForm({ ...docForm, description: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                    />
                    <input
                      type="url"
                      placeholder="URL del documento (PDF, immagine, ecc.)"
                      value={docForm.file_url}
                      onChange={(e) => setDocForm({ ...docForm, file_url: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                    />
                    <select
                      value={docForm.document_type}
                      onChange={(e) => setDocForm({ ...docForm, document_type: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                    >
                      <option value="rivista">Rivista</option>
                      <option value="report">Report</option>
                      <option value="certificato">Certificato</option>
                      <option value="altro">Altro</option>
                    </select>
                    <div className="flex gap-2">
                      <button
                        onClick={addOrgDocument}
                        className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium"
                      >
                        Salva
                      </button>
                      <button
                        onClick={() => setShowDocForm(false)}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
                      >
                        Annulla
                      </button>
                    </div>
                  </div>
                )}

                {/* Lista documenti */}
                {orgDocuments.length === 0 ? (
                  <p className="text-slate-500 text-center py-4 text-sm">
                    Nessun documento caricato per questa organizzazione
                  </p>
                ) : (
                  <div className="space-y-2">
                    {orgDocuments.map((doc) => (
                      <div key={doc.id} className="bg-slate-900/60 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-cyan-400" />
                          <div>
                            <p className="text-white text-sm font-medium">{doc.title}</p>
                            <p className="text-xs text-slate-500">{doc.document_type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-cyan-400 hover:bg-cyan-500/20 rounded-lg"
                            title="Apri"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => deleteOrgDocument(doc.id)}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users List */}
      {activeTab === 'users' && (
        <div className="space-y-3">
          {users.length === 0 ? (
            <p className="text-slate-500 text-center py-10">Nessun utente registrato</p>
          ) : (
            users.map((usr, index) => (
              <div 
                key={usr.user_id || index} 
                className="bg-slate-800/50 rounded-xl border border-violet-500/30 p-4 cursor-pointer hover:border-violet-500/60 transition-all"
                onClick={() => setSelectedUserId(usr.user_id)}
              >
                <div className="flex items-center gap-3">
                  {/* Avatar/Logo */}
                  <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {usr.photo_url || usr.organization_logo ? (
                      <img src={usr.photo_url || usr.organization_logo} alt="" className="w-full h-full object-cover" />
                    ) : usr.user_type === 'peschereccio' ? (
                      <Ship className="w-6 h-6 text-orange-400" />
                    ) : usr.user_type !== 'volontario' ? (
                      <Building2 className="w-6 h-6 text-violet-400" />
                    ) : (
                      <User className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{usr.name || 'Senza nome'}</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 mt-1">
                      <span className={`px-2 py-0.5 rounded ${
                        usr.user_type === 'peschereccio' ? 'bg-orange-500/20 text-orange-400' :
                        usr.user_type !== 'volontario' ? 'bg-violet-500/20 text-violet-400' :
                        'bg-cyan-500/20 text-cyan-400'
                      }`}>
                        {usr.user_type === 'volontario' ? 'Volontario' :
                         usr.user_type === 'peschereccio' ? 'Peschereccio' :
                         usr.user_type === 'azienda' ? 'Azienda' :
                         usr.user_type === 'ente' ? 'Ente Pubblico' :
                         usr.user_type === 'associazione' ? 'Associazione' :
                         usr.user_type === 'fondazione' ? 'Fondazione' : usr.user_type || 'Volontario'}
                      </span>
                      {usr.organization_name && (
                        <span className="text-slate-300">{usr.organization_name}</span>
                      )}
                      {usr.team_size > 1 && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {usr.team_size} persone
                        </span>
                      )}
                    </div>
                    {usr.updated_at && (
                      <p className="text-xs text-slate-500 mt-1">
                        Registrato: {formatDate(usr.updated_at)}
                      </p>
                    )}
                  </div>

                  {/* View & Delete Buttons */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedUserId(usr.user_id); }}
                    className="p-2 text-cyan-400 hover:bg-cyan-500/20 rounded-lg transition-all flex-shrink-0"
                    title="Vedi dettagli"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteUser(usr.user_id); }}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all flex-shrink-0"
                    title="Elimina utente"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Collections List */}
      {activeTab === 'collections' && (
        <div className="space-y-3">
          {collections.length === 0 ? (
            <p className="text-slate-500 text-center py-10">Nessuna raccolta trovata</p>
          ) : (
            collections.map((col) => (
              <div key={col.id} className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-800/80"
                  onClick={() => toggleExpand(col.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <span className="text-white font-medium truncate">{col.volunteer_name || 'Anonimo'}</span>
                      <span className="text-cyan-400 font-bold">{col.kg_collected} kg</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(col.timestamp)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedCollectionId(col.id); }}
                      className="p-2 text-amber-400 hover:bg-amber-500/20 rounded-lg transition-all"
                      title="Modifica raccolta"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteCollection(col.id); }}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
                      title="Elimina raccolta"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    {expandedItems[col.id] ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>
                
                {expandedItems[col.id] && (
                  <div className="px-4 pb-4 pt-2 border-t border-slate-700 space-y-2">
                    {col.place && (
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <MapPin className="w-4 h-4 text-emerald-400" />
                        <span>{col.place}</span>
                      </div>
                    )}
                    {col.location && (
                      <div className="text-xs text-slate-500">
                        GPS: {col.location.lat?.toFixed(5)}, {col.location.lng?.toFixed(5)}
                      </div>
                    )}
                    {col.photo_url && (
                      <img src={col.photo_url} alt="Foto raccolta" className="w-full max-w-xs rounded-lg mt-2" />
                    )}
                    <div className="text-xs text-slate-500 mt-2">
                      ID Utente: {col.user_id?.substring(0, 12)}...
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* SOS List */}
      {activeTab === 'sos' && (
        <div className="space-y-3">
          {sosReports.length === 0 ? (
            <p className="text-slate-500 text-center py-10">Nessun report SOS trovato</p>
          ) : (
            sosReports.map((sos) => (
              <div key={sos.id} className="bg-slate-800/50 rounded-xl border border-red-500/30 overflow-hidden">
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-800/80"
                  onClick={() => toggleExpand(`sos-${sos.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      <span className="text-red-400 font-bold uppercase">{sos.category}</span>
                      <span className="text-white truncate">- {sos.volunteer_name || 'Anonimo'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(sos.timestamp)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteSOS(sos.id); }}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
                      title="Elimina SOS"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    {expandedItems[`sos-${sos.id}`] ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>
                
                {expandedItems[`sos-${sos.id}`] && (
                  <div className="px-4 pb-4 pt-2 border-t border-slate-700 space-y-2">
                    {sos.message && (
                      <p className="text-sm text-slate-300">{sos.message}</p>
                    )}
                    {sos.animal_status && (
                      <div className="text-sm text-slate-400">Stato: {sos.animal_status}</div>
                    )}
                    {sos.location && (
                      <div className="text-xs text-slate-500">
                        GPS: {sos.location.lat?.toFixed(5)}, {sos.location.lng?.toFixed(5)}
                      </div>
                    )}
                    {sos.photo_url && (
                      <img src={sos.photo_url} alt="Foto SOS" className="w-full max-w-xs rounded-lg mt-2" />
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Events List */}
      {activeTab === 'events' && (
        <div className="space-y-3">
          {events.length === 0 ? (
            <p className="text-slate-500 text-center py-10">Nessun evento trovato</p>
          ) : (
            events.map((event) => (
              <div key={event.id} className="bg-slate-800/50 rounded-xl border border-violet-500/30 overflow-hidden">
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-800/80"
                  onClick={() => toggleExpand(`event-${event.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-violet-400 flex-shrink-0" />
                      <span className="text-white font-medium truncate">{event.title}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <User className="w-3 h-3" />
                      <span>{event.organizer}</span>
                      <span>•</span>
                      <span>{event.event_date} {event.event_time}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteEvent(event.id); }}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
                      title="Elimina evento"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    {expandedItems[`event-${event.id}`] ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>
                
                {expandedItems[`event-${event.id}`] && (
                  <div className="px-4 pb-4 pt-2 border-t border-slate-700 space-y-2">
                    {event.location_name && (
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <MapPin className="w-4 h-4 text-violet-400" />
                        <span>{event.location_name}</span>
                      </div>
                    )}
                    {event.description && (
                      <p className="text-sm text-slate-400">{event.description}</p>
                    )}
                    <div className="text-xs text-slate-500">
                      Creato da: {event.created_by?.substring(0, 12)}...
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Tab Classifica Storica (solo admin) */}
      {activeTab === 'leaderboard' && (
        <LeaderboardTab token={token} />
      )}

      {/* Tab Missioni (solo admin) */}
      {activeTab === 'missions' && (
        <MissionsTab token={token} />
      )}

      {/* User Detail Modal */}
      {selectedUserId && (
        <UserDetailModal 
          userId={selectedUserId} 
          token={token}
          onClose={() => setSelectedUserId(null)}
          onUpdate={fetchData}
          onEditCollection={(collectionId) => setSelectedCollectionId(collectionId)}
        />
      )}

      {/* Collection Edit Modal */}
      {selectedCollectionId && (
        <CollectionEditModal
          collectionId={selectedCollectionId}
          token={token}
          onClose={() => setSelectedCollectionId(null)}
          onUpdate={fetchData}
        />
      )}
    </div>
  );
};

export default AdminSection;
